import React, { CSSProperties, ReactNode } from 'react'
import classNames from 'classnames'

export type SkeletonVariant = 'text' | 'rectangular' | 'rounded' | 'circular'

export type SkeletonAnimation = 'pulse' | 'wave' | false

export interface SkeletonProps {
  /**
   * The variant of the skeleton
   * @default 'text'
   */
  variant?: SkeletonVariant
  width?: number | string
  height?: number | string
  /**
   * Animation type for the skeleton
   * @default 'pulse'
   */
  animation?: SkeletonAnimation
  className?: string
  children?: ReactNode
  [key: string]: unknown
}

const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width,
  height,
  animation = 'pulse',
  className = '',
  children,
  ...props
}) => {
  const getWidthStyle = (width: number | string | undefined): CSSProperties => {
    if (!width) return {}
    if (typeof width === 'string') {
      return { width }
    }
    if (typeof width === 'number') {
      return { width: `${width}px` }
    }
    return {}
  }

  const getHeightStyle = (height: number | string | undefined): CSSProperties => {
    if (!height) return {}
    if (typeof height === 'string') {
      return { height }
    }
    if (typeof height === 'number') {
      return { height: `${height}px` }
    }
    return {}
  }

  // Base skeleton styles with Tailwind
  const baseClasses = classNames(
    'bg-neutral-150 relative overflow-hidden',
    {
      'animate-pulse': animation === 'pulse',
      'skeleton-wave-effect': animation === 'wave',
      'animate-none': animation === false,
      'rounded-sm': variant === 'text',
      rounded: variant === 'rectangular',
      'rounded-lg': variant === 'rounded',
      'rounded-full': variant === 'circular',
      'h-4': variant === 'text' && !height,
      'w-full': variant === 'text' && !width,
    },
    className
  )

  const dimensionStyles: CSSProperties = {
    ...getWidthStyle(width),
    ...getHeightStyle(height),
  }

  if (children) {
    return (
      <span className={classNames(baseClasses, 'inline-block')} style={dimensionStyles} {...props}>
        <span className="opacity-0">{children}</span>
      </span>
    )
  }

  return <span className={baseClasses} style={dimensionStyles} {...props} />
}

export default Skeleton
export { Skeleton }
